// loadRemote.js
// Loads a project's Module Federation remote (built by seedbot-base-project) and returns
// its exposed app component. Uses the classic container flow: inject remoteEntry.js, init
// the shared scope, then container.get("./app").
//
// The container name is constant across projects (see REMOTE_NAME in
// app/seed/views/webpack.config.mjs); a given presentation renders one flow at a time,
// so window[REMOTE_CONTAINER] always holds the active flow's container.

const REMOTE_CONTAINER = "seedbot_project_views";

const scriptPromises = {};

function loadRemoteEntryScript(url) {
  if (scriptPromises[url]) return scriptPromises[url];

  scriptPromises[url] = new Promise((resolve, reject) => {
    const element = document.createElement("script");
    element.src = url;
    element.type = "text/javascript";
    element.async = true;
    element.crossOrigin = "anonymous";
    element.onload = () => resolve();
    element.onerror = () => {
      delete scriptPromises[url];
      reject(new Error(`Failed to load remote entry: ${url}`));
    };
    document.head.appendChild(element);
  });

  return scriptPromises[url];
}

// Every project's remote uses the same container name, so loading a second project's
// remoteEntry overwrites window[REMOTE_CONTAINER]. Capture each container by URL the
// moment its script runs; otherwise, after switching flows, the app would be requested
// from whichever container happened to load last and fail to resolve.
const containersByUrl = {};

async function getContainer(remoteEntryUrl, containerName) {
  if (containersByUrl[remoteEntryUrl]) return containersByUrl[remoteEntryUrl];

  await loadRemoteEntryScript(remoteEntryUrl);

  const container = window[containerName];
  if (!container) {
    delete scriptPromises[remoteEntryUrl];
    throw new Error(`Remote container "${containerName}" not found after loading ${remoteEntryUrl}`);
  }

  // __webpack_init_sharing__ / __webpack_share_scopes__ come from the host's
  // ModuleFederationPlugin (see craco.config.js). init() runs once per container:
  // re-initialising is what wires the shared React singleton into the remote.
  // eslint-disable-next-line no-undef, camelcase
  await __webpack_init_sharing__("default");
  // eslint-disable-next-line no-undef, camelcase
  await container.init(__webpack_share_scopes__.default);

  containersByUrl[remoteEntryUrl] = container;
  return container;
}

export async function loadRemoteComponent(remoteEntryUrl, exposedModule, containerName = REMOTE_CONTAINER) {
  const container = await getContainer(remoteEntryUrl, containerName);

  const factory = await container.get(exposedModule);
  if (typeof factory !== "function")
    throw new Error(`Remote "${remoteEntryUrl}" does not expose "${exposedModule}"`);

  const module = factory();
  return module.default || module;
}
